import React, { Component } from 'react';
import { Button } from 'react-bootstrap';
import Select, { components, defaultTheme } from 'react-select';
import PropTypes from 'prop-types';
import AsyncCreatableSelect from 'react-select/async-creatable';
import plus from '../../../../assets/images/qualifications-plus.svg';
import plusAlt from '../../../../assets/images/qualifications-plus-alt.svg';
import plusDisabled from '../../../../assets/images/qualifications-plus-disabled.svg';
import x from '../../../../assets/images/x.svg';
import xDark from '../../../../assets/images/x-dark.svg';
import './ExpertiseTagDropdown.scss';

const { colors } = defaultTheme;

const maxItems = 100;

const displayOtherText = (model) => {
  switch (model) {
    case 'higher_level_role':
      return 'Can\'t find what you\'re looking for? Select "Other" to proceed.';
    case 'area_of_expertise':
      return 'Can\'t find what you\'re looking for? Type and Select "Other" to proceed.';
    default:
      return 'Click "Not Applicable" if this section is not relevant in candidate evaluation.';
  }
};

const selectStyles = {
  control: (provided) => ({
    ...provided,
    minWidth: 240,
    maxWidth: 400,
    margin: 8,
  }),
  menu: () => ({
    maxWidth: 400,
  }),
};

// styled components

const menuFooterStyle = {
  padding: '8px 12px',
  background: '#f2f3f7',
  color: '#5d5d5d',
  maxWidth: 400,
};

function CheckboxOption(props) {
  const { description } = props.options.find((opt) => opt.value === props.value);
  return (
    <components.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        name={`option-${props.value}`}
        className="checkbox-option"
      />
      <label
        className="medium-weight checkbox-option-label pl-5"
        htmlFor={`option-${props.value}`}
      >
        {props.label}
      </label>
      <div className="body-text-2 ms-3 checkbox-option-description">
        {description}
      </div>
    </components.Option>
  );
}

function Menu(props) {
  const { children, isCreatable, showFooter } = props;
  const { props: { 'data-model': dataModel } } = children;

  return (
    <div
      css={{
        backgroundColor: 'white',
        borderRadius: 4,
        marginTop: 8,
        position: 'absolute',
        zIndex: 2,
      }}
    >
      {children}
      { showFooter
        ? (
          <div style={menuFooterStyle}>
            {isCreatable
              ? "Can't find it from the list? Type your keyword to create a new one."
              : displayOtherText(dataModel)}
          </div>
        ) : ''}
    </div>
  );
}
Menu.propTypes = {
  children: PropTypes.node.isRequired,
  isCreatable: PropTypes.bool.isRequired,
  showFooter: PropTypes.bool.isRequired,
};

function Blanket(props) {
  return (
    <div
      css={{
        bottom: 0,
        left: 0,
        top: 0,
        right: 0,
        position: 'fixed',
        zIndex: 1,
      }}
      {...props}
    />
  );
}
function Dropdown({
  children, isOpen, target, onClose, isCreatable, showMenuFooter,
}) {
  return (
    <div
      className={`expertise-tag-dropdown-container ${isOpen ? 'open' : ''}`}
    >
      {target}
      {isOpen ? (
        <Menu
          isCreatable={isCreatable}
          showFooter={showMenuFooter}
        >
          {children}
        </Menu>
      ) : null}
      {isOpen ? <Blanket onClick={onClose} /> : null}
    </div>
  );
}
Dropdown.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isCreatable: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  target: PropTypes.node.isRequired,
  showMenuFooter: PropTypes.bool.isRequired,
};

function Svg(p) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      focusable="false"
      role="presentation"
      {...p}
    />
  );
}
function DropdownIndicator() {
  return (
    <div css={{ color: colors.neutral20, height: 24, width: 32 }} className="me-2">
      <Svg>
        <path
          d={'M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 '
          + '7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z'}
          fill="currentColor"
          fillRule="evenodd"
        />
      </Svg>
    </div>
  );
}

const groupStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};
const groupBadgeStyles = {
  backgroundColor: '#EBECF0',
  borderRadius: '2em',
  color: '#172B4D',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 'normal',
  lineHeight: '1',
  minWidth: 1,
  padding: '0.16666666666667em 0.5em',
  textAlign: 'center',
};

const formatGroupLabel = (data) => (
  <div style={groupStyles}>
    <span>{data.label}</span>
    <span style={groupBadgeStyles}>{data.options.length}</span>
  </div>
);

const formatGroupOptions = (options) => {
  const grouped = options.reduce((result, item) => {
    const category = item.category || 'Others'; // Default to 'Others' if category is missing

    if (!result[category]) {
      result[category] = [];
    }

    result[category].push(item);
    return result;
  }, {});

  // Step 2: Convert grouped data to the required format
  return Object.keys(grouped).map((category) => ({
    label: category,
    options: grouped[category],
  }));
};

class ExpertiseTagDropdown extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: props.menuIsOpen,
    };

    this.handleRemoveSelection = this.handleRemoveSelection.bind(this);
  }

  handleRemoveSelection(val) {
    const {
      handleSelect, selection, model, multi,
    } = this.props;

    return () => {
      const newSelection = [...selection].filter((item) => item.value !== val);
      handleSelect(model, multi)(newSelection);
    };
  }

  renderTags() {
    const { selection, model, handleRemove } = this.props;

    if (!selection) return null;

    return selection.map((item, idx) => (
      <div className={`qualification-tag ${model}-tag-display`} key={`${item.value}-${idx}`}>
        <div className="expertise-tag-label">
          { item.label }
        </div>
        <div
          className="remove-link"
          role="button"
          tabIndex={0}
          onClick={handleRemove
            ? () => handleRemove(item.value) : this.handleRemoveSelection(item.value)}
        >
          <img src={x} alt="remove" />
        </div>
      </div>
    ));
  }

  renderButton() {
    const {
      model, isDisabled, selection, heading, menuHeading, multi, ctaVerb, plusColor,
    } = this.props;
    const { isOpen } = this.state;

    const disabled = isDisabled || (!multi && selection && selection.length);
    let plusImg = plus;
    if (disabled) {
      plusImg = plusDisabled;
    } else if (plusColor === 'alt') {
      plusImg = plusAlt;
    }

    if (isOpen) {
      return (
        <div className="open-dropdown-heading">
          <div className="open-dropdown-text">
            {menuHeading || (multi ? 'Select 1 or more options' : 'Select 1 option')}
          </div>
          <div
            className="close-dropdown"
            onClick={() => this.setState({ isOpen: false })}
            role="button"
            tabIndex={0}
          >
            <img alt="close" src={xDark} />
          </div>
        </div>
      );
    }
    return (
      <Button
        className={`expertise-tag-dropdown-button add-${model}-button ${disabled && 'disabled'}`}
        onClick={() => !disabled && this.setState({ isOpen: !isOpen })}
      >
        <img src={plusImg} alt="add" />
        <div className={`caption ${disabled && 'disabled'}`}>
          { ctaVerb }
          {' '}
          { heading }
        </div>
      </Button>
    );
  }

  render() {
    const {
      model, selection, handleSelect, multi, handleCreate,
      isValidNewOption, loadOptions, heading, isDisabled,
      picklist, isCreatable, checkboxOptions, showSubHeading,
      showMenuFooter, bodyClass, groupStyle,
    } = this.props;
    const { isOpen } = this.state;
    const selectComponents = { DropdownIndicator, IndicatorSeparator: () => null };
    if (checkboxOptions) {
      selectComponents.Option = CheckboxOption;
    }

    return (
      <div className={bodyClass}>
        <div className="expertise-tag-dropdown-heading">
          {heading}
          {['area_of_expertise', 'higher_level_role', 'level'].includes(model)
            && <div className="heading-required">* Required</div>}
          { heading === 'Role' && showSubHeading
            && (
            <div className="input-sub-heading">
              Roles are used to standardize project titles across the life sciences.
              <br />
              Example: Data Management Leader
            </div>
            )}
        </div>
        <div className="tags-container">
          {this.renderTags()}
        </div>
        <Dropdown
          isOpen={isOpen}
          onClose={() => this.setState({ isOpen: false })}
          target={this.renderButton()}
          isCreatable={isCreatable}
          showMenuFooter={showMenuFooter}
        >
          {groupStyle
            ? (
              <Select
                autoFocus
                onBlur={() => this.setState({ isOpen: false })}
                tabIndex={0}
                backspaceRemovesValue={false}
                components={selectComponents}
                controlShouldRenderValue={false}
                hideSelectedOptions={false}
                isClearable={false}
                menuIsOpen={isOpen}
                styles={selectStyles}
                data-model={model}
                classNamePrefix="clora-multiselect"
                closeMenuOnSelect={false}
                value={selection}
                limit={multi ? null : 1}
                className="project-expertise-select"
                id={`project-expertise-select-${model}`}
                onChange={(newValue) => {
                  handleSelect(model, multi)(newValue);
                  this.setState({ isOpen: false });
                }}
                tabSelectsValue={false}
                isDisabled={isDisabled}
                placeholder="Search..."
                marginTop="5"
                defaultOptions={picklist.slice(0, maxItems)}
                isValidNewOption={isValidNewOption}
                onCreateOption={handleCreate(model, multi)}
                isMulti
                formatGroupLabel={formatGroupLabel}
                options={formatGroupOptions(picklist)}
              />
            )
            : (
              <AsyncCreatableSelect
                autoFocus
                onBlur={() => this.setState({ isOpen: false })}
                tabIndex={0}
                backspaceRemovesValue={false}
                components={selectComponents}
                controlShouldRenderValue={false}
                hideSelectedOptions={false}
                isClearable={false}
                menuIsOpen={isOpen}
                styles={selectStyles}
                data-model={model}
                classNamePrefix="clora-multiselect"
                closeMenuOnSelect={false}
                value={selection}
                limit={multi ? null : 1}
                className="project-expertise-select"
                id={`project-expertise-select-${model}`}
                onChange={(newValue) => {
                  handleSelect(model, multi)(newValue);
                  this.setState({ isOpen: false });
                }}
                tabSelectsValue={false}
                isDisabled={isDisabled}
                placeholder="Search..."
                marginTop="5"
                defaultOptions={picklist.slice(0, maxItems)}
                cacheOptions
                isValidNewOption={isValidNewOption}
                loadOptions={loadOptions}
                onCreateOption={handleCreate(model, multi)}
                isMulti
              />
            )}
        </Dropdown>
      </div>
    );
  }
}

ExpertiseTagDropdown.propTypes = {
  model: PropTypes.string.isRequired,
  selection: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleSelect: PropTypes.func.isRequired,
  handleCreate: PropTypes.func,
  handleRemove: PropTypes.func,
  multi: PropTypes.bool,
  isValidNewOption: PropTypes.func,
  isCreatable: PropTypes.bool,
  heading: PropTypes.string,
  menuHeading: PropTypes.string,
  isDisabled: PropTypes.bool,
  menuIsOpen: PropTypes.bool,
  picklist: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loadOptions: PropTypes.func.isRequired,
  checkboxOptions: PropTypes.bool,
  showSubHeading: PropTypes.bool,
  showMenuFooter: PropTypes.bool,
  groupStyle: PropTypes.bool,
  ctaVerb: PropTypes.string,
  bodyClass: PropTypes.string,
  plusColor: PropTypes.string,
};

ExpertiseTagDropdown.defaultProps = {
  multi: true,
  handleCreate: () => {},
  isValidNewOption: () => {},
  heading: '',
  isDisabled: false,
  menuIsOpen: false,
  isCreatable: false,
  menuHeading: null,
  checkboxOptions: false,
  showSubHeading: true,
  showMenuFooter: true,
  ctaVerb: 'Add ',
  bodyClass: 'mt-2 mb-2',
  plusColor: 'default',
  handleRemove: null,
  groupStyle: false,
};

export default ExpertiseTagDropdown;
